import React, { useEffect, useState } from 'react';
import { InjectedRouter } from 'react-router';
import { withRouter } from 'app/context/Router';
import ConfirmDeviceHeader from 'auth/components/ConfirmDeviceHeader';
import ConfirmDeviceForm from 'auth/components/ConfirmDeviceForm';
import ConfirmDeviceFooter from 'auth/components/ConfirmDeviceFooter';
import AuthActions from 'auth/AuthActions';
import { redirector } from 'auth/components/SignInPage';
import ToastActions from 'actions/ToastActions';
import Device from 'entities/Device';
import EventTracking from 'lib/EventTracking';
import { MouseEventHandler } from 'types/typescript/eventHandlers';
import errorLogger from 'utils/errorLogger';
import MagicLinkPoller from 'auth/components/MagicLinkPoller';

export type DeviceType = 'email' | 'phone';

type Props = {
  uid: string;
  deviceType: DeviceType;
  needsPassword: boolean;
  onGoBack?: MouseEventHandler;
  needsConfirmationErrorCode?: string;
  onConfirmationDone?: () => void;
  router: InjectedRouter;
};

function ConfirmDevice(props: Props) {
  const {
    uid,
    deviceType,
    needsPassword,
    onGoBack,
    needsConfirmationErrorCode,
    onConfirmationDone,
    router,
  } = props;

  const [pollingSecret, setPollingSecret] = useState<string | null>(null);
  const [pusherChannel, setPusherChannel] = useState<string | null>(null);

  const requestVerification = () => {
    return Device.outboundVerify(uid).then(
      (resp) => {
        setPollingSecret(resp.polling_secret);
        setPusherChannel(resp.pusher_channel);
        ToastActions.showSuccessToast('Verification steps sent');
      },
      () => ToastActions.showErrorToast('Unable to send verification steps')
    );
  };

  const onSuccess = ({ token }: { token: string }): Promise<void> =>
    AuthActions.accessTokenSignIn(token)
      .then(() => {
        const { attemptedTransition, onSignInRedirectTo } = redirector;
        if (attemptedTransition) {
          redirector.reset();
          router.push(attemptedTransition);
          return;
        }

        if (onSignInRedirectTo) {
          redirector.reset();
          router.push(onSignInRedirectTo);
          return;
        }

        if (onConfirmationDone) {
          onConfirmationDone();
          return;
        }

        router.push('/');
      })
      .catch(errorLogger('ConfirmDevice sign in failed'));

  useEffect(() => {
    requestVerification();
    EventTracking.trackKey(`log_in.confirm_${deviceType}_page.view`);
    // adding `requestVerification` to the dependency array causes sending of multiple emails & ultimately an error toast when trying to login, see this convo for context: https://remind.slack.com/archives/C03G66JEQ/p1701207108401509
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, deviceType]);

  return (
    <>
      <ConfirmDeviceHeader
        uid={uid}
        deviceType={deviceType}
        needsPassword={needsPassword}
        onGoBack={onGoBack}
        needsConfirmationErrorCode={needsConfirmationErrorCode}
      />
      {pollingSecret && pusherChannel && (
        <MagicLinkPoller
          onSuccess={onSuccess}
          pollingSecret={pollingSecret}
          pusherChannel={pusherChannel}
        />
      )}
      <ConfirmDeviceForm
        uid={uid}
        deviceType={deviceType}
        onSubmitCode={(uid, code) => Device.createOutboundDevice(uid, code).then(onSuccess)}
      />
      <ConfirmDeviceFooter deviceType={deviceType} onResendCode={() => requestVerification()} />
    </>
  );
}

export function getReadableDeviceType(device: DeviceType) {
  if (device === 'email') {
    return 'email address';
  } else {
    return device;
  }
}

export default withRouter(ConfirmDevice);
